<template>
  <div class="sale-details fade-in">
    <!-- Header Section -->
    <div class="sale-header card shadow-sm border-0 mb-4">
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-6">
            <h3 class="text-primary mb-2">
              <i class="bi bi-receipt me-2"></i>Sale Details
            </h3>
            <h5 class="text-muted mb-3" v-if="!loading">{{ sale.store.code }} - {{ sale.store.name }}</h5>
            <h5 class="mb-2" v-if="!loading">{{ sale.reference }}</h5>

            <div class="d-flex flex-wrap gap-3 mb-2">
              <div class="sale-info-item">
                <i class="bi bi-person text-primary me-1"></i>
                <span class="text-muted">Created by:</span>
                <span class="fw-medium ms-1" v-if="!loading">{{ sale.user.first_name }} {{ sale.user.last_name }}</span>
              </div>

              <div class="sale-info-item">
                <i class="bi bi-calendar-date text-primary me-1"></i>
                <span class="text-muted">Created at:</span>
                <span class="fw-medium ms-1" v-if="!loading">{{ formatDate(sale.created_at) }}</span>
              </div>
            </div>
          </div>

          <div class="col-md-6 text-md-end mt-3 mt-md-0">
            <Badge pill :type="getSaleStatus(sale.status)" class="status-badge">
              {{ sale.status }}
            </Badge>

            <Badge v-if="sale.return_status" pill :type="getSaleReturnStatus(sale.return_status)" class="status-badge ms-2">
              {{ sale.return_status }}
            </Badge>

            <div class="mt-3 d-flex flex-wrap justify-content-md-end gap-2">
              <button class="btn btn-outline-secondary" @click="$router.go(-1)">
                <i class="bi bi-arrow-left me-1"></i> Back
              </button>
              <button class="btn btn-outline-primary" v-print="'#printMe'">
                <i class="bi bi-printer me-1"></i> Print Receipt
              </button>
              <button
                v-if="sale.total - totalTransactions > 0"
                class="btn btn-success"
                @click="transactionModal = true"
              >
                <i class="bi bi-plus-circle me-1"></i>Add Payment
              </button>
              <button
                v-if="sale.status !== 'returned' && isReturnable"
                class="btn btn-warning"
                @click="openReturnModal"
              >
                <i class="bi bi-arrow-return-left me-1"></i>Return Items
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Products Card -->
    <Card body-classes="p-0" class="mb-5 shadow-sm border-0">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <i class="bi bi-box-seam me-2 text-primary"></i>
            <span class="fw-medium">Sale Products</span>
            <Badge pill type="primary" class="ms-2">{{saleProducts.length}}</Badge>
          </div>
          <UInput v-model="dataTableSearch" size="md" placeholder="Search products" class="mb-0 search-input"/>
        </div>
      </template>
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading products...</p>
      </div>
      <div v-if="!loading" class="product-table">
        <DataTable show-filter-buttons :data="productData" :headers="dataTableHeaders"
                   :search-keyword="dataTableSearch" :per-page="dataTablePerPage" :current-page="dataTablePage">
          <template v-slot:default="{row}">
            <Cell
                v-for="(cellData, key) in row"
                :key="key"
                class="text-nowrap"
                :class="[
                    row['returned'] > 0 ? 'bg-danger-subtle' : '',
                ]"
                :cell-classes="[
                    ['status', 'tax'].indexOf(key.toString().toLowerCase()) !== -1?'d-flex justify-content-center':'',
                ]">
              <Badge v-if="key === 5" pill type="info">
                {{cellData}}
              </Badge>
              <span v-else>{{cellData}}</span>
            </Cell>
          </template>
        </DataTable>
        <div class="d-flex flex-wrap pt-3 pb-4 align-items-end mx-4 justify-content-between">
          <div class="fw-bold">
            <div class="d-flex flex-wrap gap-3 align-items-center">
              <div class="d-flex align-items-center mb-2">
                <div class="me-3">
                <span class="badge bg-info rounded-circle p-2 me-2">
                  <i class="bi bi-receipt-cutoff"></i>
                </span>
                </div>
                <div>
                  <h6 class="mb-0">Tax Total</h6>
                  <p class="mb-0 h5 text-success">{{ formatNumber(sale.tax) }}</p>
                </div>
              </div>
              <div class="d-flex align-items-center mb-2">
                <div class="me-3">
                <span class="badge bg-primary rounded-circle p-2 me-2">
                  <i class="bi bi-cash-stack"></i>
                </span>
                </div>
                <div>
                  <h6 class="mb-0">Grand Total</h6>
                  <p class="mb-0 h5 text-success">{{ formatNumber(sale.total) }}</p>
                </div>
              </div>
            </div>
          </div>
          <div class="me-sm-4 mb-sm-0 mb-3">
            <p class="mb-2 small">Rows per page</p>
            <select data="" class="form-select" v-model="dataTablePerPage">
              <option v-for="item in [5, 10, 25, 50, 100]" :key="item" :value="item">{{item}}</option>
            </select>
          </div>
          <div>
            <Pagination v-model="dataTablePage" :pages="Math.ceil(saleProducts.length/dataTablePerPage)" :threshold="2">
              <template v-slot:next>Next</template>
              <template v-slot:previous>Prev</template>
            </Pagination>
          </div>
        </div>
      </div>
    </Card>

    <!-- Transactions Card -->
    <Card body-classes="p-0" class="mb-5 shadow-sm border-0">
      <template v-slot:header>
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <i class="bi bi-credit-card me-2 text-primary"></i>
            <span class="fw-medium">Payment Transactions</span>
            <Badge pill type="primary" class="ms-2">{{sale.transactions.length}}</Badge>
          </div>
          <div>
            <button
                v-if="sale.total - totalTransactions > 0"
                class="btn btn-success"
                @click="transactionModal = true"
            >
              <i class="bi bi-plus-circle me-1"></i>Add Payment
            </button>
          </div>
        </div>
      </template>
      <div v-if="loading" class="text-center py-5">
        <Spinner spinner-class="mb-1" />
        <p class="text-muted mt-2">Loading transactions...</p>
      </div>
      <div v-if="!loading" class="table-responsive">
        <table class="table table-hover align-middle mb-0">
          <thead>
          <tr>
            <th>AMOUNT</th>
            <th>METHOD</th>
            <th>REFERENCE</th>
            <th>DATE</th>
            <th>STATUS</th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="sale.transactions.length === 0">
            <td colspan="5" class="text-center py-4 text-muted">
              <i class="bi bi-inbox fs-4 d-block mb-2"></i>
              No transactions recorded for this sale.
            </td>
          </tr>
          <tr v-for="(transaction, index) in sale.transactions" :key="transaction.id" :class="{'bg-light': index % 2 === 0}">
            <td class="fw-bold">{{ formatNumber(transaction.amount) }}</td>
            <td>
              <Badge pill :type="transaction.method === 'cash' ? 'success' : 'primary'">
                {{ transaction.method.toUpperCase() }}
              </Badge>
            </td>
            <td>
              <span v-if="transaction.reference" class="badge bg-light text-dark">
                {{ transaction.reference }}
              </span>
              <span v-else class="text-muted">-</span>
            </td>
            <td>{{ formatDate(transaction.created_at) }}</td>
            <td>
              <Badge pill type="success">
                COMPLETED
              </Badge>
            </td>
          </tr>
          </tbody>
          <tfoot class="fw-bold total-row">
            <tr>
              <td>{{ formatNumber(totalTransactions) }}</td>
              <td colspan="2"></td>
              <td class="text-end">Balance:</td>
              <td>
                <span :class="sale.total - totalTransactions > 0 ? 'text-danger' : 'text-success'">
                  {{ formatNumber(sale.total - totalTransactions) }}
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- Payment Summary Card -->
      <div v-if="!loading && sale.transactions.length > 0" class="card-footer bg-white py-4 border-top">
        <div class="row">
          <div class="col-md-6">
            <div class="d-flex align-items-center mb-2">
              <div class="me-3">
                <span class="badge bg-primary rounded-circle p-2">
                  <i class="bi bi-cash-stack"></i>
                </span>
              </div>
              <div>
                <h6 class="mb-0">Total Paid</h6>
                <p class="mb-0 h5 text-success">{{ formatNumber(totalTransactions) }}</p>
              </div>
            </div>
          </div>
          <div class="col-md-6">
            <div class="d-flex align-items-center mb-2">
              <div class="me-3">
                <span class="badge bg-secondary rounded-circle p-2">
                  <i class="bi bi-wallet2"></i>
                </span>
              </div>
              <div>
                <h6 class="mb-0">Balance</h6>
                <p class="mb-0 h5" :class="sale.total - totalTransactions > 0 ? 'text-danger' : 'text-success'">
                  {{ formatNumber(sale.total - totalTransactions) }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>

    <!-- Return Modal -->
    <Modal :open="returnModal" @closed="returnModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-arrow-return-left me-2 text-primary"></i>Process Sale Return
        </h5>
        <button @click="returnModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="form-group col-md-12 mb-3">
            <label for="reason" class="form-label fw-medium mb-2">Reason for Return:</label>
            <textarea id="reason" class="form-control" v-model="returnReason" placeholder="Enter reason for return"></textarea>
          </div>

          <h6 class="mt-4 mb-3">Products to Return</h6>
          <div class="table-responsive">
            <table class="table table-bordered">
              <thead>
                <tr>
                  <th>Product</th>
                  <th class="text-center">Sold Qty</th>
                  <th class="text-center">Return Qty</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="product in returnProducts" :key="product.id">
                  <td>{{ product.product ? product.product.name : product.name }}</td>
                  <td class="text-center">{{ product.quantity }}</td>
                  <td>
                    <input
                        type="number"
                        class="form-control form-control-sm"
                        min="0"
                        :max="product.quantity"
                        v-model.number="product.return_qty"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" @click="returnModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="submitReturn">
          <i class="bi bi-check-circle me-1"></i>Submit Return
        </button>
      </div>
    </Modal>
    <!-- Return Modal End -->

    <!-- Add Transaction Modal -->
    <Modal :open="transactionModal" @closed="transactionModal = false" dialog-size="modal-lg">
      <div class="modal-header">
        <h5 class="modal-title m-0">
          <i class="bi bi-plus-circle me-2 text-primary"></i>Add Payment Transaction
        </h5>
        <button @click="transactionModal = false" type="button" class="btn-close outline-none" data-dismiss="modal" aria-label="Close">
        </button>
      </div>
      <div class="modal-body">
        <form @submit.prevent="">
          <div class="row">
            <div class="form-group col-md-6">
              <label for="method" class="form-label fw-medium mb-2">Payment Method:</label>
              <select id="method" class="form-select" v-model="transaction.method" data="">
                <option value="cash">Cash</option>
                <option value="mpesa">MPESA</option>
              </select>
            </div>
            <div class="form-group col-md-6">
              <label for="amount" class="form-label fw-medium mb-2">Amount:</label>
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-cash"></i></span>
                <input
                  id="amount"
                  type="number"
                  class="form-control"
                  :min="sale.total - totalTransactions"
                  placeholder="Enter amount"
                  v-model="transaction.amount"
                  autofocus="autofocus"
                >
              </div>
              <small class="text-muted">Remaining balance: {{ formatNumber(sale.total - totalTransactions) }}</small>
            </div>
            <div v-if="transaction.method === 'mpesa'" class="form-group col-md-12 mt-3">
              <label for="reference" class="form-label fw-medium mb-2">MPESA Reference:</label>
              <div class="input-group">
                <span class="input-group-text"><i class="bi bi-upc"></i></span>
                <input
                  id="reference"
                  type="text"
                  class="form-control"
                  placeholder="Enter MPESA reference"
                  v-model="transaction.reference"
                >
              </div>
            </div>
            <div class="col-md-12 mt-3">
              <div class="alert alert-info py-2 mb-0">
                <i class="bi bi-info-circle me-2"></i>
                <span>After adding this payment, the remaining balance will be:
                  <strong>{{ formatNumber(sale.total - totalTransactions - (transaction.amount || 0)) }}</strong>
                </span>
              </div>
            </div>
          </div>
        </form>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-outline-secondary" data-dismiss="modal" @click="transactionModal = false">
          <i class="bi bi-x-circle me-1"></i>Cancel
        </button>
        <button type="button" class="btn btn-primary" @click="addTransaction">
          <i class="bi bi-check-circle me-1"></i>Add Payment
        </button>
      </div>
    </Modal>
    <!-- Add Transaction Modal End -->

    <!-- Receipt -->
    <div class="d-none">
      <div id="printMe">
        <Receipt :sale="sale" :products="saleProducts" :organization="organization"></Receipt>
      </div>
    </div>
    <!-- Receipt End -->
  </div>
</template>

<script>
import formatNumber from "@/utils/formatNumber";
import {decrypt} from "@/utils/crypto";
import saleService from "@/api/saleService";
import formatDate from "../../utils/formatDate";
import notify from "@/utils/notify";
import transactionService from "@/api/transactionService";
import Receipt from "@/components/receipt/Receipt.vue";
import print from 'vue3-print-nb';
import Badge from "@/components/badge/Badge.vue";
import DataTable from "@/components/datatable/DataTable.vue";
import Cell from "@/components/datatable/Cell.vue";
import Pagination from "@/components/pagination/Pagination.vue";
import UInput from "@/components/input/Input.vue";

export default {
  name: "SaleDetails",
  components: {
    Receipt,
    Badge,
    DataTable,
    Cell,
    Pagination,
    UInput
  },
  directives: {
    print
  },

  data(){
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    return {
      profile: {},
      store: {},
      organization: {},
      sale: {
        user: {},
        store: {},
        transactions: []
      },
      saleProducts: [],
      loading: true,
      transactionModal: false,
      returnModal: false,
      returnReason: '',
      returnProducts: [],
      transaction: {
        organization_id: this.organization.id,
        sale_id: this.$route.params.id,
        amount: 0,
        method: 'cash',
        reference: '',
      },
      subscription: {
        package: {}
      },
      // DataTable properties
      dataTableHeaders: [
        'SKU',
        'Code',
        'Name',
        'Quantity',
        'Price',
        'Tax',
        'Total',
        'Returned'
      ],
      dataTableSearch: '',
      dataTablePage: 1,
      dataTablePerPage: 10,
    }
  },

  methods:{
    formatDate,
    formatNumber,
    async getSaleDetails() {
      this.saleProducts = await saleService.getSaleDetails(this.$route.params.id)
      this.loading = false
    },

    openReturnModal() {
      this.returnProducts = JSON.parse(JSON.stringify(this.saleProducts)).map(p => ({
        ...p,
        name: p.product ? p.product.name : p.name,
        return_qty: 0,
      }));
      this.returnModal = true;
    },

    async submitReturn() {
      const productsToReturn = this.returnProducts
          .filter(p => {
            const returnQty = Number(p.return_qty); // Ensure numeric conversion
            const availableQty = Number(p.quantity);
            return returnQty > 0 && returnQty <= availableQty;
          })
          .map(p => ({
            sale_detail_id: p.id,
            quantity: Number(p.return_qty), // Ensure numeric conversion
          }));

      if (!this.returnReason) {
        return notify.alert.error('Please provide a reason for the return.');
      }

      if (productsToReturn.length === 0) {
        return notify.alert.error('Please specify a valid quantity for at least one product to return.');
      }

      const payload = {
        sale_id: this.sale.id,
        reason: this.returnReason,
        products: productsToReturn,
      };

      try {
        let response = await saleService.createReturn(payload);
        if (response) {
          this.returnModal = false;
          this.returnReason = '';
          await this.getSaleDetails();
        }
      } catch (error) {
        notify.error(error.response?.data?.message || 'Failed to process return.');
      }
    },

    getSaleStatus(status) {
      switch (status) {
        case 'complete':
          return 'success';
        case 'due':
          return 'primary';
        default:
          return 'secondary';
      }
    },
    getSaleReturnStatus(status) {
      switch (status) {
        case 'partially_returned':
          return 'warning';
        default:
          return 'secondary';
      }
    },
    async addTransaction() {
      if (this.transaction.method === 'mpesa') {
        if (this.transaction.reference.length < 10) {
          notify.alert.info('Please enter valid MPESA reference', 2000)
          return;
        }
      }
      let response = await transactionService.createTransaction(this.transaction)
      if (response) {
        this.transactionModal = false
        await this.getSaleDetails()
        window.location.reload()
      }
    }
  },

  computed:{
    totalTransactions(){
      if (this.sale.transactions.length > 0){
        return this.sale.transactions.reduce((acc, current) => {
          return acc + current.amount
        }, 0)
      } else {
        return 0
      }
    },

    productData(){
      return this.saleProducts.map(product => {
        return {
          sku: product.product.sku,
          code: product.product.code,
          name: product.product.name,
          quantity: product.quantity,
          price: this.formatNumber(product.price),
          tax: this.formatNumber(product.tax),
          total: this.formatNumber(product.total),
          returned: product.returned_quantity
        }
      })
    },

    isReturnable() {
      const saleDate = new Date(this.sale.created_at);
      const currentDate = new Date();
      const diffTime = Math.abs(currentDate - saleDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays < 2;
    }
  },

  async created(){
    this.profile = JSON.parse(decrypt(sessionStorage.getItem('profile')))
    this.store = JSON.parse(decrypt(sessionStorage.getItem('store')))
    this.organization = JSON.parse(decrypt(sessionStorage.getItem('organization')))
    this.sale = await saleService.getSale(this.$route.params.id)
    await this.getSaleDetails()
    this.transaction.amount = this.sale.total - this.totalTransactions
    this.subscription = JSON.parse(decrypt(sessionStorage.getItem('subscription')))
  },

  watch: {
    'transaction.reference': function () {
      this.transaction.reference = this.transaction.reference.toUpperCase()
    },
    'transaction.method': function () {
      if (this.transaction.method === 'cash') {
        this.transaction.reference = ''
      }
    }
  }
}
</script>

<style scoped>
/* General styles */
.sale-details {
  padding: 0.5rem;
}

.fade-in {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Header styles */
.sale-header {
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.sale-header:hover {
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1) !important;
}

.sale-info-item {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.status-badge {
  font-size: 0.85rem;
  padding: 0.5em 1em;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Table styles */
.table th {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.7rem;
  letter-spacing: 0.5px;
  color: #6b7280;
  border-top: none;
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  border-bottom: 1px solid #e9ecef;
}

.table td {
  padding: 0.75rem 1.5rem;
  vertical-align: middle;
  border-color: #e9ecef;
  color: #4b5563;
  font-size: 0.9rem;
}

.total-row {
  background-color: #f8f9fa;
  border-top: 2px solid #dee2e6;
}

.search-input {
  min-width: 250px;
}

/* Modal styles */
.modal-header {
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

/* Product table */
.product-table {
  overflow-x: auto;
}
</style>
